import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { FiX, FiChevronLeft, FiChevronRight, FiSettings } from 'react-icons/fi';
import { galleryAPI } from '../utils/api';
import SEO from '../components/SEO';
import './Gallery.css';

const Gallery = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get('category') || 'all';
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [visibleItems, setVisibleItems] = useState(new Set());
  const itemRefs = useRef({});

  const categories = ['all', 'personal', 'video-fashion', 'photo-fashion', 'reels', 'weddings'];
  const categoryLabels = {
    all: 'All Work',
    personal: 'Personal Photoshoot',
    'video-fashion': 'Video: Fashion/Beauty Campaigns',
    'photo-fashion': 'Photo: Fashion/Beauty Campaigns',
    reels: 'Reels',
    weddings: 'Weddings'
  };

  useEffect(() => {
    loadGalleryItems();
  }, []);

  const loadGalleryItems = async () => {
    try {
      const res = await galleryAPI.getAll();
      setGalleryItems(res.items || []);
    } catch (error) {
      console.error('Failed to load gallery items:', error);
      setGalleryItems([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    const category = searchParams.get('category') || 'all';
    setSelectedCategory(category);
  }, [searchParams]);

  const filteredItems = selectedCategory === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === selectedCategory);

  useEffect(() => {
    setVisibleItems(new Set());
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => new Set([...prev, entry.target.dataset.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const timeoutId = setTimeout(() => {
      Object.values(itemRefs.current).forEach((ref) => {
        if (ref) observer.observe(ref);
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [filteredItems]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxImage(filteredItems[index]);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    document.body.style.overflow = 'unset';
  };

  const navigateLightbox = (direction) => {
    let newIndex = lightboxIndex + direction;
    if (newIndex < 0) newIndex = filteredItems.length - 1;
    if (newIndex >= filteredItems.length) newIndex = 0;
    setLightboxIndex(newIndex);
    setLightboxImage(filteredItems[newIndex]);
  };

  useEffect(() => {
    if (!lightboxImage) return;

    const handleKeyPress = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigateLightbox(-1);
      if (e.key === 'ArrowRight') navigateLightbox(1);
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxImage, lightboxIndex]);

  if (loading) {
    return (
      <div className="gallery-page">
        <SEO title="Gallery" description="Photography gallery - weddings, fashion, portraits by Prague photographer Marta Zborovska. Prague, Czech Republic." path="/gallery" />
        <div className="gallery-empty">
          <p>Loading gallery...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-page">
      <SEO title="Gallery" description="Photography gallery - weddings, fashion, portraits by Prague photographer Marta Zborovska. Prague, Czech Republic." path="/gallery" />
      <div className="gallery-filters">
        <div className="container">
          <div className="filter-buttons">
            {categories.map(category => (
              <button
                key={category}
                className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => {
                  setSelectedCategory(category);
                  if (category === 'all') {
                    setSearchParams({});
                  } else {
                    setSearchParams({ category });
                  }
                }}
              >
                {categoryLabels[category]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <div className="gallery-empty">
          <h2>No items yet</h2>
          <p>Add gallery items from the Admin Panel.</p>
          <Link to="/admin" className="admin-link">
            <FiSettings /> Go to Admin Panel
          </Link>
        </div>
      ) : (
        <div className="gallery-container">
          <div className="gallery-content">
            {filteredItems.map((item, index) => (
              <div
                key={item._id}
                ref={(el) => (itemRefs.current[item._id] = el)}
                data-id={item._id}
                className={`gallery-post ${visibleItems.has(String(item._id)) ? 'visible' : ''}`}
              >
                <div className="gallery-post-layout">
                  <div className="gallery-post-header">
                    <h2 className="gallery-post-title">{item.title}</h2>
                    {item.description && <p className="gallery-post-description">{item.description}</p>}
                  </div>
                  <div className="gallery-post-images">
                    {(item.images || []).map((img, imgIndex) => (
                      <div 
                        key={imgIndex}
                        className="gallery-image-wrapper"
                        onClick={() => openLightbox(index)}
                      >
                        <img 
                          src={img.url} 
                          alt={item.title}
                          className="gallery-image"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {lightboxImage && lightboxImage.images && lightboxImage.images[0] && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">
            <FiX />
          </button>
          <button 
            className="lightbox-nav lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              navigateLightbox(-1);
            }}
            aria-label="Previous image"
          >
            <FiChevronLeft />
          </button>
          <button 
            className="lightbox-nav lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              navigateLightbox(1);
            }}
            aria-label="Next image"
          >
            <FiChevronRight />
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <div className="lightbox-image-container">
              <img 
                src={lightboxImage.images[0].url} 
                alt={lightboxImage.title}
                className="lightbox-image"
              />
            </div>
            <div className="lightbox-info">
              <h3>{lightboxImage.title}</h3>
              <p>{categoryLabels[lightboxImage.category]}</p>
              <span className="lightbox-counter">
                {lightboxIndex + 1} / {filteredItems.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
